import React, { useEffect, useState } from "react";
import { Plus, RefreshCw, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "../components/layout/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { api, extractApiError } from "../lib/api";
import { DEFAULT_CAPABILITIES, normalizeCapabilities } from "../lib/capabilities";
import { formatDateTime } from "../lib/format";
import { useAuth } from "../lib/auth-context";
import { ROLE_LABELS } from "../lib/labels";
import { useSystemBranding } from "../lib/system-branding-context";
import AppBrandMark from "../components/branding/AppBrandMark";

const EMPTY_FORM = {
  name: "",
  branding_color: "#0F172A",
  default_project: "",
  sender_name: "",
  reply_to_email: "",
};

const EMPTY_CREATE_FORM = {
  name: "",
  slug: "",
  branding_color: "#0F172A",
  default_project: "",
  sender_name: "",
  reply_to_email: "",
};

const EMPTY_INVITE_FORM = {
  full_name: "",
  email: "",
  role: "VIEWER",
};

const EMPTY_CAPABILITIES_FORM = {
  ...DEFAULT_CAPABILITIES,
  max_public_forms: "0",
  email_quota_monthly: "",
};

const ADDABLE_ROLES = ["TENANT_ADMIN", "SALES_USER", "VIEWER"];

function detailToForm(detail) {
  return {
    name: detail?.name || "",
    branding_color: detail?.branding_color || "#0F172A",
    default_project: detail?.default_project || "",
    sender_name: detail?.sender_name || "",
    reply_to_email: detail?.reply_to_email || "",
  };
}

function detailToCapabilitiesForm(detail) {
  const capabilities = normalizeCapabilities(detail?.capabilities);
  return {
    ...capabilities,
    max_public_forms: String(capabilities.max_public_forms ?? 0),
    email_quota_monthly:
      capabilities.email_quota_monthly === null || capabilities.email_quota_monthly === undefined
        ? ""
        : String(capabilities.email_quota_monthly),
  };
}

function validateCapabilitiesForm(form) {
  const maxPublicForms = Number(form.max_public_forms || 0);
  if (!Number.isInteger(maxPublicForms) || maxPublicForms < 0) {
    return "Max Public Forms muss eine ganze Zahl >= 0 sein.";
  }
  if ((form.email_quota_monthly || "").trim()) {
    const emailQuotaMonthly = Number(form.email_quota_monthly);
    if (!Number.isInteger(emailQuotaMonthly) || emailQuotaMonthly < 0) {
      return "E-Mail-Kontingent monatlich muss eine ganze Zahl >= 0 sein.";
    }
  }
  if (!form.fyrefunnel_enabled && (form.public_forms_enabled || form.widget_embed_enabled)) {
    return "Public Forms und Widget Embed setzen FyreFunnel voraus.";
  }
  return "";
}

function capabilitiesFormToPayload(form) {
  return {
    crm_core_enabled: Boolean(form.crm_core_enabled),
    fyrefunnel_enabled: Boolean(form.fyrefunnel_enabled),
    public_forms_enabled: Boolean(form.public_forms_enabled),
    max_public_forms: Number(form.max_public_forms || 0),
    widget_embed_enabled: Boolean(form.widget_embed_enabled),
    mail_audit_enabled: Boolean(form.mail_audit_enabled),
    email_tracking_allowed: Boolean(form.email_tracking_allowed),
    marketing_consent_allowed: Boolean(form.marketing_consent_allowed),
    email_quota_monthly: (form.email_quota_monthly || "").trim() ? Number(form.email_quota_monthly) : null,
    automation_enabled: Boolean(form.automation_enabled),
  };
}

function maskPublicFormKey(value) {
  if (!value) return "—";
  const s = String(value);
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-6)}`;
}

function slugifyName(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  const german = raw
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
  const noAccents = german.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const separators = noAccents.replace(/&/g, "-");
  const cleaned = separators.replace(/[^a-z0-9-]+/g, "-");
  return cleaned.replace(/-+/g, "-").replace(/^-+/, "").replace(/-+$/, "");
}

function normalizeSlug(value) {
  return String(value || "").trim().toLowerCase();
}

function isValidSlug(slug) {
  if (!slug) return false;
  if (slug.length < 2 || slug.length > 64) return false;
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
}

function suggestSlugAlternatives(baseSlug, existingSlugs) {
  const base = normalizeSlug(baseSlug);
  if (!base) return [];
  const out = [];
  for (let i = 2; i <= 20; i += 1) {
    const candidate = `${base}-${i}`;
    if (!existingSlugs.has(candidate)) out.push(candidate);
    if (out.length >= 3) break;
  }
  return out;
}

function normalizeOptional(value) {
  const v = String(value || "").trim();
  return v ? v : null;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const base64 = result.includes(",") ? result.slice(result.indexOf(",") + 1) : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden."));
    reader.readAsDataURL(file);
  });
}

export default function SuperAdminTenantsPage() {
  const { isSuperAdmin, user: currentUser, tenant, switchTenant } = useAuth();
  const { reloadBranding } = useSystemBranding();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [capabilitiesSaving, setCapabilitiesSaving] = useState(false);
  const [capabilitiesSaveError, setCapabilitiesSaveError] = useState("");
  const [nameError, setNameError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [capabilitiesForm, setCapabilitiesForm] = useState(EMPTY_CAPABILITIES_FORM);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createNameError, setCreateNameError] = useState("");
  const [createSlugError, setCreateSlugError] = useState("");
  const [createSlugTouched, setCreateSlugTouched] = useState(false);
  const [createSlugSuggestions, setCreateSlugSuggestions] = useState([]);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addSearchLoading, setAddSearchLoading] = useState(false);
  const [addSearchError, setAddSearchError] = useState("");
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [addSelectedUser, setAddSelectedUser] = useState(null);
  const [addSelectedRole, setAddSelectedRole] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE_FORM);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleTargetMember, setRoleTargetMember] = useState(null);
  const [roleNext, setRoleNext] = useState("");
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState("");
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeTargetMember, setRemoveTargetMember] = useState(null);
  const [removeSaving, setRemoveSaving] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const [userStatusOpen, setUserStatusOpen] = useState(false);
  const [userStatusTargetMember, setUserStatusTargetMember] = useState(null);
  const [userStatusSaving, setUserStatusSaving] = useState(false);
  const [userStatusError, setUserStatusError] = useState("");
  const [systemBranding, setSystemBranding] = useState({ light_logo_url: null, dark_logo_url: null });
  const [systemBrandingLoading, setSystemBrandingLoading] = useState(true);
  const [systemBrandingSaving, setSystemBrandingSaving] = useState(false);
  const [systemBrandingError, setSystemBrandingError] = useState("");

  const loadTenants = async (preferredTenantId) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/admin/tenants");
      const nextRows = response.data || [];
      setRows(nextRows);
      const nextSelectedId = nextRows.some((row) => row.id === preferredTenantId)
        ? preferredTenantId
        : nextRows[0]?.id || null;
      setSelectedTenantId(nextSelectedId);
    } catch (e) {
      setRows([]);
      setSelectedTenantId(null);
      setError(extractApiError(e, "Mandanten konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadTenants();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let ignore = false;

    (async () => {
      setSystemBrandingLoading(true);
      setSystemBrandingError("");
      try {
        const response = await api.get("/api/admin/system-branding");
        if (!ignore) setSystemBranding(response.data || { light_logo_url: null, dark_logo_url: null });
      } catch (e) {
        if (!ignore) {
          setSystemBranding({ light_logo_url: null, dark_logo_url: null });
          setSystemBrandingError(extractApiError(e, "Globales Branding konnte nicht geladen werden."));
        }
      } finally {
        if (!ignore) setSystemBrandingLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!createOpen) return;
    setCreateError("");
    setCreateNameError("");
    setCreateSlugError("");
    setCreateSlugSuggestions([]);
  }, [createOpen]);

  useEffect(() => {
    if (!addMemberOpen) return;
    setAddSearch("");
    setAddSearchError("");
    setAddSearchResults([]);
    setAddSelectedUser(null);
    setAddSelectedRole("");
    setAddSaving(false);
    setAddError("");
  }, [addMemberOpen]);

  useEffect(() => {
    if (!inviteOpen) return;
    setInviteSaving(false);
    setInviteError("");
    setInviteForm(EMPTY_INVITE_FORM);
  }, [inviteOpen]);

  useEffect(() => {
    if (!roleDialogOpen) return;
    setRoleSaving(false);
    setRoleError("");
  }, [roleDialogOpen]);

  useEffect(() => {
    if (!removeOpen) return;
    setRemoveSaving(false);
    setRemoveError("");
  }, [removeOpen]);

  useEffect(() => {
    if (!addMemberOpen) return;
    if (!selectedTenantId) return;
    const q = (addSearch || "").trim();
    if (q.length < 2) {
      setAddSearchError("");
      setAddSearchResults([]);
      setAddSearchLoading(false);
      return;
    }

    let ignore = false;
    const timer = setTimeout(async () => {
      setAddSearchLoading(true);
      setAddSearchError("");
      try {
        const response = await api.get("/api/admin/users/search", { params: { q, tenant_id: selectedTenantId } });
        if (ignore) return;
        setAddSearchResults(response.data || []);
      } catch (e) {
        if (ignore) return;
        setAddSearchResults([]);
        setAddSearchError(extractApiError(e, "Suche fehlgeschlagen."));
      } finally {
        if (!ignore) setAddSearchLoading(false);
      }
    }, 250);

    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [addMemberOpen, addSearch, selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) {
      setSelectedTenant(null);
      setForm(EMPTY_FORM);
      setCapabilitiesForm(EMPTY_CAPABILITIES_FORM);
      setDetailError("");
      setMembers([]);
      setMembersError("");
      setSaveError("");
      setCapabilitiesSaveError("");
      setNameError("");
      return;
    }

    let ignore = false;

    const loadDetail = async () => {
      setDetailLoading(true);
      setDetailError("");
      try {
        const response = await api.get(`/api/admin/tenants/${selectedTenantId}`);
        if (ignore) return;
        setSelectedTenant(response.data);
        setForm(detailToForm(response.data));
        setCapabilitiesForm(detailToCapabilitiesForm(response.data));
        setSaveError("");
        setCapabilitiesSaveError("");
        setNameError("");
        setStatusError("");
      } catch (e) {
        if (ignore) return;
        setSelectedTenant(null);
        setForm(EMPTY_FORM);
        setCapabilitiesForm(EMPTY_CAPABILITIES_FORM);
        setDetailError(extractApiError(e, "Mandanten-Details konnten nicht geladen werden."));
      } finally {
        if (!ignore) setDetailLoading(false);
      }
    };

    loadDetail();
    return () => {
      ignore = true;
    };
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    let ignore = false;

    const loadMembers = async () => {
      setMembersLoading(true);
      setMembersError("");
      try {
        const response = await api.get(`/api/admin/tenants/${selectedTenantId}/members`);
        if (ignore) return;
        setMembers(response.data || []);
      } catch (e) {
        if (ignore) return;
        setMembers([]);
        setMembersError(extractApiError(e, "Mitglieder konnten nicht geladen werden."));
      } finally {
        if (!ignore) setMembersLoading(false);
      }
    };

    loadMembers();
    return () => {
      ignore = true;
    };
  }, [selectedTenantId]);

  const reloadMembers = async () => {
    if (!selectedTenantId) return;
    setMembersLoading(true);
    setMembersError("");
    try {
      const response = await api.get(`/api/admin/tenants/${selectedTenantId}/members`);
      setMembers(response.data || []);
    } catch (e) {
      setMembers([]);
      setMembersError(extractApiError(e, "Mitglieder konnten nicht geladen werden."));
    } finally {
      setMembersLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateSlugTouched(false);
    setCreateError("");
    setCreateNameError("");
    setCreateSlugError("");
    setCreateSlugSuggestions([]);
    setCreateOpen(true);
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    const name = (createForm.name || "").trim();
    if (!name) {
      setCreateNameError("Mandanten-Name darf nicht leer sein.");
      toast.error("Mandanten-Name darf nicht leer sein.");
      return;
    }

    const slug = normalizeSlug(createForm.slug);
    if (!slug) {
      setCreateSlugError("Slug darf nicht leer sein.");
      toast.error("Slug darf nicht leer sein.");
      return;
    }
    if (!isValidSlug(slug)) {
      setCreateSlugError("Slug ist ungültig. Erlaubt sind a-z, 0-9 und Bindestriche.");
      toast.error("Slug ist ungültig.");
      return;
    }

    setCreateSaving(true);
    setCreateError("");
    setCreateNameError("");
    setCreateSlugError("");
    setCreateSlugSuggestions([]);

    try {
      const payload = {
        name,
        slug,
        branding_color: createForm.branding_color,
        default_project: normalizeOptional(createForm.default_project),
        sender_name: normalizeOptional(createForm.sender_name),
        reply_to_email: normalizeOptional(createForm.reply_to_email),
      };

      const response = await api.post("/api/admin/tenants", payload);
      toast.success("Mandant erstellt.");
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
      setCreateSlugTouched(false);
      await loadTenants(response.data?.id);
      if (response.data?.id) setSelectedTenantId(response.data.id);
    } catch (e) {
      const status = e?.response?.status;
      const msg = extractApiError(e, "Mandant konnte nicht erstellt werden.");
      setCreateError(msg);
      if (status === 409) {
        setCreateSlugError("Dieser Slug ist bereits vergeben.");
        const existing = new Set(rows.map((r) => r.slug));
        setCreateSlugSuggestions(suggestSlugAlternatives(slug, existing));
      }
      toast.error(msg);
    } finally {
      setCreateSaving(false);
    }
  };

  const handleToggleTenantStatus = async () => {
    if (!selectedTenantId || !selectedTenant) return;

    const currentlyActive = selectedTenant.is_active !== false;
    const nextActive = !currentlyActive;

    if (!nextActive) {
      const ok = window.confirm(
        "Deaktivierte Mandanten können nicht mehr von normalen Benutzern genutzt werden. Öffentliche Formulare werden blockiert. Bestehende Daten bleiben erhalten. Super-Admins können deaktivierte Mandanten weiterhin verwalten und wieder aktivieren."
      );
      if (!ok) return;
    }

    setStatusSaving(true);
    setStatusError("");
    try {
      const response = await api.patch(`/api/admin/tenants/${selectedTenantId}/status`, { is_active: nextActive });
      setSelectedTenant(response.data);
      setForm(detailToForm(response.data));
      await loadTenants(selectedTenantId);
      toast.success(nextActive ? "Mandant aktiviert." : "Mandant deaktiviert.");
    } catch (e) {
      const msg = extractApiError(e, "Status konnte nicht geändert werden.");
      setStatusError(msg);
      toast.error(msg);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!selectedTenantId) return;
    const trimmedName = (form.name || "").trim();
    if (!trimmedName) {
      setNameError("Mandanten-Name darf nicht leer sein.");
      toast.error("Mandanten-Name darf nicht leer sein.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        name: trimmedName,
        branding_color: form.branding_color,
        default_project: form.default_project,
        sender_name: form.sender_name,
        reply_to_email: form.reply_to_email,
      };
      const response = await api.patch(`/api/admin/tenants/${selectedTenantId}`, payload);
      setSelectedTenant(response.data);
      setForm(detailToForm(response.data));
      await loadTenants(selectedTenantId);
      toast.success("Mandant gespeichert.");
    } catch (e) {
      const msg = extractApiError(e, "Mandant konnte nicht gespeichert werden.");
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedTenantId) return;
    if (!addSelectedUser || !addSelectedRole) return;

    setAddSaving(true);
    setAddError("");
    try {
      await api.post(`/api/admin/tenants/${selectedTenantId}/members`, {
        user_id: addSelectedUser.id,
        role: addSelectedRole,
      });
      toast.success("Benutzer hinzugefügt.");
      setAddMemberOpen(false);
      await loadTenants(selectedTenantId);
      await reloadMembers();
    } catch (e) {
      const msg = extractApiError(e, "Benutzer konnte nicht hinzugefügt werden.");
      setAddError(msg);
      toast.error(msg);
    } finally {
      setAddSaving(false);
    }
  };

  const handleOpenRoleDialog = (member) => {
    setRoleTargetMember(member);
    setRoleNext(member?.role || "");
    setRoleError("");
    setRoleDialogOpen(true);
  };

  const handleOpenRemoveDialog = (member) => {
    setRemoveTargetMember(member);
    setRemoveError("");
    setRemoveOpen(true);
  };

  const handleOpenUserStatusDialog = (member) => {
    setUserStatusTargetMember(member);
    setUserStatusError("");
    setUserStatusOpen(true);
  };

  const handleSwitchToTenant = async (tenantRow) => {
    if (!tenantRow?.slug) return;
    const result = await switchTenant(tenantRow.slug);
    if (!result?.ok) {
      toast.error(result?.error || "Mandantenwechsel fehlgeschlagen.");
      return;
    }
    toast.success(`Aktiver Mandant wurde auf ${tenantRow.name} gesetzt.`);
    navigate("/");
  };

  const handleChangeRole = async () => {
    if (!selectedTenantId) return;
    if (!roleTargetMember?.membership_id) return;
    if (!roleNext) return;

    setRoleSaving(true);
    setRoleError("");
    try {
      await api.patch(`/api/admin/tenants/${selectedTenantId}/members/${roleTargetMember.membership_id}`, { role: roleNext });
      toast.success("Rolle aktualisiert.");
      setRoleDialogOpen(false);
      await reloadMembers();
    } catch (e) {
      const msg = extractApiError(e, "Rolle konnte nicht aktualisiert werden.");
      setRoleError(msg);
      toast.error(msg);
    } finally {
      setRoleSaving(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedTenantId) return;
    if (!removeTargetMember?.membership_id) return;

    setRemoveSaving(true);
    setRemoveError("");
    try {
      await api.delete(`/api/admin/tenants/${selectedTenantId}/members/${removeTargetMember.membership_id}`);
      toast.success("Mitglied entfernt.");
      setRemoveOpen(false);
      await loadTenants(selectedTenantId);
      await reloadMembers();
    } catch (e) {
      const msg = extractApiError(e, "Mitglied konnte nicht entfernt werden.");
      setRemoveError(msg);
      toast.error(msg);
    } finally {
      setRemoveSaving(false);
    }
  };

  const handleToggleUserStatus = async () => {
    if (!userStatusTargetMember?.user?.id) return;
    const nextActive = userStatusTargetMember.user.is_active === false;

    setUserStatusSaving(true);
    setUserStatusError("");
    try {
      await api.patch(`/api/admin/users/${userStatusTargetMember.user.id}/status`, { is_active: nextActive });
      toast.success(nextActive ? "Benutzer aktiviert." : "Benutzer deaktiviert.");
      setUserStatusOpen(false);
      await reloadMembers();
    } catch (e) {
      const msg = extractApiError(e, "Benutzerstatus konnte nicht geändert werden.");
      setUserStatusError(msg);
      toast.error(msg);
    } finally {
      setUserStatusSaving(false);
    }
  };

  const handleCapabilitiesSave = async () => {
    if (!selectedTenantId) return;
    const validationError = validateCapabilitiesForm(capabilitiesForm);
    if (validationError) {
      setCapabilitiesSaveError(validationError);
      toast.error(validationError);
      return;
    }
    setCapabilitiesSaving(true);
    setCapabilitiesSaveError("");
    try {
      const payload = capabilitiesFormToPayload(capabilitiesForm);
      const response = await api.patch(`/api/admin/tenants/${selectedTenantId}/capabilities`, payload);
      setSelectedTenant((current) => ({
        ...(current || {}),
        capabilities: response.data,
      }));
      setCapabilitiesForm(detailToCapabilitiesForm({ capabilities: response.data }));
      toast.success("Module & Limits gespeichert.");
    } catch (e) {
      const msg = extractApiError(e, "Module & Limits konnten nicht gespeichert werden.");
      setCapabilitiesSaveError(msg);
      toast.error(msg);
    } finally {
      setCapabilitiesSaving(false);
    }
  };

  const updateCapabilityToggle = (key, checked) => {
    setCapabilitiesForm((current) => {
      const next = {
        ...current,
        [key]: Boolean(checked),
      };
      if (key === "fyrefunnel_enabled" && !checked) {
        next.public_forms_enabled = false;
        next.widget_embed_enabled = false;
      }
      if (key === "public_forms_enabled" && !checked && Number(current.max_public_forms || 0) < 1) {
        next.max_public_forms = "0";
      }
      return next;
    });
    if (capabilitiesSaveError) setCapabilitiesSaveError("");
  };

  const updateCapabilityValue = (key, value) => {
    setCapabilitiesForm((current) => ({
      ...current,
      [key]: value,
    }));
    if (capabilitiesSaveError) setCapabilitiesSaveError("");
  };

  const handleInviteMember = async () => {
    if (!selectedTenantId) return;

    const full_name = (inviteForm.full_name || "").trim();
    const email = (inviteForm.email || "").trim();
    const role = inviteForm.role;
    if (!full_name || !email || !ADDABLE_ROLES.includes(role)) return;

    setInviteSaving(true);
    setInviteError("");
    try {
      await api.post(`/api/admin/tenants/${selectedTenantId}/invites`, {
        full_name,
        email,
        role,
      });
      toast.success("Einladung versendet.");
      setInviteOpen(false);
      setInviteForm(EMPTY_INVITE_FORM);
    } catch (e) {
      const msg = extractApiError(e, "Einladung konnte nicht erstellt werden.");
      setInviteError(msg);
      toast.error(msg);
    } finally {
      setInviteSaving(false);
    }
  };

  const addSubmitDisabled =
    addSaving ||
    !addSelectedUser ||
    !addSelectedRole ||
    !ADDABLE_ROLES.includes(addSelectedRole) ||
    addSelectedUser?.already_member ||
    addSelectedUser?.is_active === false;

  const inviteSubmitDisabled =
    inviteSaving ||
    !(inviteForm.full_name || "").trim() ||
    !(inviteForm.email || "").trim() ||
    !ADDABLE_ROLES.includes(inviteForm.role);

  const refreshSystemBranding = async () => {
    setSystemBrandingLoading(true);
    setSystemBrandingError("");
    try {
      const response = await api.get("/api/admin/system-branding");
      setSystemBranding(response.data || { light_logo_url: null, dark_logo_url: null });
    } catch (e) {
      setSystemBranding({ light_logo_url: null, dark_logo_url: null });
      setSystemBrandingError(extractApiError(e, "Globales Branding konnte nicht geladen werden."));
    } finally {
      setSystemBrandingLoading(false);
    }
  };

  const handleSystemLogoUpload = async (variant, file) => {
    if (!file) return;
    setSystemBrandingSaving(true);
    setSystemBrandingError("");
    try {
      const dataBase64 = await fileToBase64(file);
      const response = await api.put(`/api/admin/system-branding/logo/${variant}`, {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        data_base64: dataBase64,
      });
      setSystemBranding(response.data || { light_logo_url: null, dark_logo_url: null });
      await reloadBranding();
      toast.success("Logo gespeichert.");
    } catch (e) {
      setSystemBrandingError(extractApiError(e, "Logo konnte nicht gespeichert werden."));
      toast.error(extractApiError(e, "Logo konnte nicht gespeichert werden."));
    } finally {
      setSystemBrandingSaving(false);
    }
  };

  const handleSystemLogoRemove = async (variant) => {
    setSystemBrandingSaving(true);
    setSystemBrandingError("");
    try {
      const response = await api.delete(`/api/admin/system-branding/logo/${variant}`);
      setSystemBranding(response.data || { light_logo_url: null, dark_logo_url: null });
      await reloadBranding();
      toast.success("Logo entfernt.");
    } catch (e) {
      setSystemBrandingError(extractApiError(e, "Logo konnte nicht entfernt werden."));
      toast.error(extractApiError(e, "Logo konnte nicht entfernt werden."));
    } finally {
      setSystemBrandingSaving(false);
    }
  };

  const roleSubmitDisabled =
    roleSaving ||
    !roleTargetMember ||
    !roleNext ||
    !ADDABLE_ROLES.includes(roleNext) ||
    roleNext === roleTargetMember?.role;

  return (
    <div>
      <PageHeader
        title="Mandanten"
        description="Super-Admin-Verwaltung für grundlegende Tenant-Daten."
        actions={(
          <div className="flex items-center gap-2">
            <Button onClick={handleOpenCreate} disabled={loading}>
              <Plus className="w-4 h-4" /> Mandant erstellen
            </Button>
            <Button variant="outline" onClick={() => loadTenants(selectedTenantId)} disabled={loading}>
              <RefreshCw className="w-4 h-4" /> {loading ? "Lädt …" : "Neu laden"}
            </Button>
          </div>
        )}
      />

      <Card className="mb-6">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Globales FyreCRM-Branding</CardTitle>
            <CardDescription>
              Globales App- und Login-Branding für FyreCRM. Ohne Upload bleibt das bestehende Fallback aktiv.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={refreshSystemBranding}
            disabled={systemBrandingLoading || systemBrandingSaving}
          >
            <RefreshCw className="w-4 h-4" /> {systemBrandingLoading ? "Lädt …" : "Neu laden"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {systemBrandingError ? (
            <Alert variant="destructive">
              <AlertTitle>Branding konnte nicht geladen werden</AlertTitle>
              <AlertDescription>{systemBrandingError}</AlertDescription>
            </Alert>
          ) : null}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SystemBrandingSlot
              title="Logo Light Mode"
              description="Wird im Light Mode von Login und App verwendet."
              logoUrl={systemBranding.light_logo_url}
              disabled={systemBrandingSaving}
              onUpload={(file) => handleSystemLogoUpload("light", file)}
              onRemove={() => handleSystemLogoRemove("light")}
            />
            <SystemBrandingSlot
              title="Logo Dark Mode"
              description="Wird im Dark Mode von Login und App verwendet."
              logoUrl={systemBranding.dark_logo_url}
              disabled={systemBrandingSaving}
              onUpload={(file) => handleSystemLogoUpload("dark", file)}
              onRemove={() => handleSystemLogoRemove("dark")}
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Fallback: Wenn kein Logo gesetzt ist, bleibt das bestehende Default-Logo sichtbar.
          </div>
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Mandant erstellen</DialogTitle>
            <DialogDescription>
              Erstelle einen neuen Mandanten. Der Slug wird beim Erstellen festgelegt und ist danach unveränderlich.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {createError && (
              <div className="md:col-span-2">
                <Alert variant="destructive">
                  <AlertTitle>Erstellung fehlgeschlagen</AlertTitle>
                  <AlertDescription>{createError}</AlertDescription>
                </Alert>
              </div>
            )}

            <Field label="Name *">
              <Input
                value={createForm.name}
                onChange={(event) => {
                  const v = event.target.value;
                  setCreateForm((current) => ({
                    ...current,
                    name: v,
                    slug: createSlugTouched ? current.slug : slugifyName(v),
                  }));
                  if (createNameError && v.trim()) setCreateNameError("");
                }}
                placeholder="Mandanten-Name"
                autoFocus
              />
              {createNameError && <div className="text-xs text-destructive">{createNameError}</div>}
            </Field>

            <Field label="Slug *">
              <Input
                value={createForm.slug}
                onChange={(event) => {
                  const v = event.target.value;
                  setCreateForm((current) => ({ ...current, slug: v }));
                  setCreateSlugTouched(true);
                  setCreateSlugSuggestions([]);
                  if (createSlugError && v.trim()) setCreateSlugError("");
                }}
                placeholder="z. B. blum-informatik-gmbh"
                className="font-mono"
              />
              <div className="text-xs text-muted-foreground">
                Der Slug ist nach der Erstellung unveränderlich.
              </div>
              {createSlugError && <div className="text-xs text-destructive">{createSlugError}</div>}
              {createSlugSuggestions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {createSlugSuggestions.map((s) => (
                    <Button
                      key={s}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCreateForm((current) => ({ ...current, slug: s }));
                        setCreateSlugTouched(true);
                        setCreateSlugSuggestions([]);
                      }}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              )}
            </Field>

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-[140px_minmax(0,1fr)] gap-4">
              <Field label="Branding-Farbe">
                <input
                  type="color"
                  value={createForm.branding_color || "#0F172A"}
                  onChange={(event) => setCreateForm((current) => ({ ...current, branding_color: event.target.value }))}
                  className="h-11 w-full cursor-pointer rounded-lg border border-border bg-card"
                />
              </Field>
              <Field label="HEX-Wert">
                <Input
                  value={createForm.branding_color}
                  onChange={(event) => setCreateForm((current) => ({ ...current, branding_color: event.target.value }))}
                  className="font-mono"
                  placeholder="#0F172A"
                />
              </Field>
            </div>

            <Field label="Standard-Projekt">
              <Input
                value={createForm.default_project}
                onChange={(event) => setCreateForm((current) => ({ ...current, default_project: event.target.value }))}
                placeholder="z. B. OptiMES Prime"
              />
            </Field>

            <Field label="Absender für E-Mails">
              <Input
                value={createForm.sender_name}
                onChange={(event) => setCreateForm((current) => ({ ...current, sender_name: event.target.value }))}
                placeholder="Absendername"
              />
            </Field>

            <Field label="Reply-To E-Mail">
              <Input
                value={createForm.reply_to_email}
                onChange={(event) => setCreateForm((current) => ({ ...current, reply_to_email: event.target.value }))}
                placeholder="reply@firma.de"
              />
            </Field>

            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={createSaving}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={createSaving}>
                {createSaving ? "Speichern …" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bestehenden Benutzer hinzufügen</DialogTitle>
            <DialogDescription>
              Es werden nur bestehende Benutzer verknüpft. Es wird kein neuer Benutzer erstellt und keine Einladung verschickt.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedTenant?.is_active === false ? (
              <Alert>
                <AlertTitle>Hinweis</AlertTitle>
                <AlertDescription>Der ausgewählte Mandant ist inaktiv. Super-Admins können trotzdem Mitglieder hinzufügen.</AlertDescription>
              </Alert>
            ) : null}

            {addError ? (
              <Alert variant="destructive">
                <AlertTitle>Hinzufügen fehlgeschlagen</AlertTitle>
                <AlertDescription>{addError}</AlertDescription>
              </Alert>
            ) : null}

            <Field label="Suche">
              <Input
                value={addSearch}
                onChange={(event) => {
                  const v = event.target.value;
                  setAddSearch(v);
                  if (addSelectedUser && addSelectedUser.email && !v.trim()) setAddSelectedUser(null);
                }}
                placeholder="E-Mail oder Name suchen"
                autoFocus
              />
              <div className="text-xs text-muted-foreground">Mindestens 2 Zeichen.</div>
            </Field>

            {addSearchError ? (
              <Alert variant="destructive">
                <AlertTitle>Suche fehlgeschlagen</AlertTitle>
                <AlertDescription>{addSearchError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-2">
              <div className="text-sm font-medium">Ergebnisse</div>
              {addSearchLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Sucht …</div>
              ) : addSearch.trim().length >= 2 && addSearchResults.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Keine Treffer.</div>
              ) : addSearchResults.length > 0 ? (
                <div className="obsidian overflow-hidden rounded-lg border border-border">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border bg-muted/40">
                        <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                          <th className="px-4 py-3 font-medium">Name</th>
                          <th className="px-4 py-3 font-medium">E-Mail</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {addSearchResults.map((u) => {
                          const isSelected = addSelectedUser?.id === u.id;
                          return (
                            <tr
                              key={u.id}
                              role="button"
                              tabIndex={0}
                              className={isSelected ? "bg-muted/30 cursor-pointer" : "cursor-pointer"}
                              onClick={() => setAddSelectedUser(u)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") setAddSelectedUser(u);
                              }}
                            >
                              <td className="px-4 py-3 font-medium">
                                <div className="flex items-center gap-2">
                                  <span>{u.full_name || "-"}</span>
                                  {u.is_super_admin ? <Badge variant="outline">Super-Admin</Badge> : null}
                                  {u.already_member ? <Badge variant="secondary">Bereits Mitglied</Badge> : null}
                                </div>
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{u.email || "-"}</td>
                              <td className="px-4 py-3">
                                {u.is_active === false ? (
                                  <Badge variant="destructive">Inaktiv</Badge>
                                ) : (
                                  <Badge variant="secondary">Aktiv</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="py-6 text-center text-sm text-muted-foreground">Suche starten, um Ergebnisse zu sehen.</div>
              )}
            </div>

            <Field label="Rolle">
              <Select value={addSelectedRole} onValueChange={setAddSelectedRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Rolle wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TENANT_ADMIN">{ROLE_LABELS.TENANT_ADMIN}</SelectItem>
                  <SelectItem value="SALES_USER">{ROLE_LABELS.SALES_USER}</SelectItem>
                  <SelectItem value="VIEWER">{ROLE_LABELS.VIEWER}</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground">Super-Admin ist keine Mandantenrolle.</div>
            </Field>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddMemberOpen(false)} disabled={addSaving}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleAddMember} disabled={addSubmitDisabled}>
              {addSaving ? "Hinzufügen …" : "Zum Mandanten hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Benutzer einladen</DialogTitle>
            <DialogDescription>
              Es wird eine Einladung für einen neuen Tenant-Benutzer erstellt. Der Benutzer setzt sein Passwort über den Einladungslink.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedTenant?.is_active === false ? (
              <Alert>
                <AlertTitle>Hinweis</AlertTitle>
                <AlertDescription>Der ausgewählte Mandant ist inaktiv. Super-Admins können trotzdem Einladungen erstellen.</AlertDescription>
              </Alert>
            ) : null}

            {inviteError ? (
              <Alert variant="destructive">
                <AlertTitle>Einladung fehlgeschlagen</AlertTitle>
                <AlertDescription>{inviteError}</AlertDescription>
              </Alert>
            ) : null}

            <Field label="Name">
              <Input
                value={inviteForm.full_name}
                onChange={(event) => setInviteForm((current) => ({ ...current, full_name: event.target.value }))}
                placeholder="Vor- und Nachname"
                autoFocus
              />
            </Field>

            <Field label="E-Mail">
              <Input
                value={inviteForm.email}
                onChange={(event) => setInviteForm((current) => ({ ...current, email: event.target.value }))}
                placeholder="name@firma.de"
              />
            </Field>

            <Field label="Rolle">
              <Select value={inviteForm.role} onValueChange={(value) => setInviteForm((current) => ({ ...current, role: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Rolle wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TENANT_ADMIN">{ROLE_LABELS.TENANT_ADMIN}</SelectItem>
                  <SelectItem value="SALES_USER">{ROLE_LABELS.SALES_USER}</SelectItem>
                  <SelectItem value="VIEWER">{ROLE_LABELS.VIEWER}</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground">SUPER_ADMIN ist keine Mandantenrolle.</div>
            </Field>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} disabled={inviteSaving}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleInviteMember} disabled={inviteSubmitDisabled}>
              {inviteSaving ? "Einladung wird erstellt …" : "Einladung senden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Rolle ändern</DialogTitle>
            <DialogDescription>
              Diese Änderung betrifft nur die Rolle in diesem Mandanten. Globale Super-Admin-Rechte werden nicht verändert.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedTenant?.is_active === false ? (
              <Alert>
                <AlertTitle>Hinweis</AlertTitle>
                <AlertDescription>Der ausgewählte Mandant ist inaktiv. Super-Admins können Rollen trotzdem ändern.</AlertDescription>
              </Alert>
            ) : null}

            {roleError ? (
              <Alert variant="destructive">
                <AlertTitle>Änderung fehlgeschlagen</AlertTitle>
                <AlertDescription>{roleError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="grid grid-cols-1 gap-2 rounded-lg border border-border bg-muted/20 p-4 text-sm">
              <InfoRow label="Name" value={roleTargetMember?.user?.full_name || "-"} />
              <InfoRow label="E-Mail" value={roleTargetMember?.user?.email || "-"} mono />
              <InfoRow label="Aktuelle Rolle" value={ROLE_LABELS[roleTargetMember?.role] || roleTargetMember?.role || "-"} />
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Status</span>
                <div className="flex items-center gap-2">
                  {roleTargetMember?.user?.is_super_admin ? <Badge variant="outline">Super-Admin</Badge> : null}
                  {roleTargetMember?.user?.is_active === false ? (
                    <Badge variant="destructive">Inaktiv</Badge>
                  ) : (
                    <Badge variant="secondary">Aktiv</Badge>
                  )}
                </div>
              </div>
            </div>

            <Field label="Neue Rolle">
              <Select value={roleNext} onValueChange={setRoleNext}>
                <SelectTrigger>
                  <SelectValue placeholder="Rolle wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TENANT_ADMIN">{ROLE_LABELS.TENANT_ADMIN}</SelectItem>
                  <SelectItem value="SALES_USER">{ROLE_LABELS.SALES_USER}</SelectItem>
                  <SelectItem value="VIEWER">{ROLE_LABELS.VIEWER}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRoleDialogOpen(false)} disabled={roleSaving}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleChangeRole} disabled={roleSubmitDisabled}>
              {roleSaving ? "Speichern …" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={removeOpen}
        onOpenChange={(open) => {
          if (removeSaving) return;
          setRemoveOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mitglied entfernen</AlertDialogTitle>
            <AlertDialogDescription>
              Dies entfernt nur die Mitgliedschaft in diesem Mandanten. Der Benutzer selbst wird nicht gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            {removeError ? (
              <Alert variant="destructive">
                <AlertTitle>Entfernen fehlgeschlagen</AlertTitle>
                <AlertDescription>{removeError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="grid grid-cols-1 gap-2 rounded-lg border border-border bg-muted/20 p-4 text-sm">
              <InfoRow label="Name" value={removeTargetMember?.user?.full_name || "-"} />
              <InfoRow label="E-Mail" value={removeTargetMember?.user?.email || "-"} mono />
              <InfoRow label="Rolle" value={ROLE_LABELS[removeTargetMember?.role] || removeTargetMember?.role || "-"} />
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Status</span>
                <div className="flex items-center gap-2">
                  {removeTargetMember?.user?.is_super_admin ? <Badge variant="outline">Super-Admin</Badge> : null}
                  {removeTargetMember?.user?.is_active === false ? (
                    <Badge variant="destructive">Inaktiv</Badge>
                  ) : (
                    <Badge variant="secondary">Aktiv</Badge>
                  )}
                </div>
              </div>
            </div>

            <Alert>
              <AlertTitle>Hinweis</AlertTitle>
              <AlertDescription>Der Benutzer kann danach ggf. keinen Mandantenzugriff mehr haben.</AlertDescription>
            </Alert>

            {removeTargetMember?.user?.is_super_admin ? (
              <Alert>
                <AlertTitle>Hinweis</AlertTitle>
                <AlertDescription>Globale Super-Admin-Rechte bleiben unverändert.</AlertDescription>
              </Alert>
            ) : null}

            {currentUser?.id && removeTargetMember?.user?.id && currentUser.id === removeTargetMember.user.id ? (
              <Alert>
                <AlertTitle>Hinweis</AlertTitle>
                <AlertDescription>
                  Du entfernst deine Mitgliedschaft in diesem Mandanten. Globale Super-Admin-Rechte bleiben bestehen.
                </AlertDescription>
              </Alert>
            ) : null}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSaving}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={removeSaving}
              onClick={(event) => {
                event.preventDefault();
                handleRemoveMember();
              }}
            >
              {removeSaving ? "Entfernen …" : "Entfernen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={userStatusOpen}
        onOpenChange={(open) => {
          if (userStatusSaving) return;
          setUserStatusOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {userStatusTargetMember?.user?.is_active === false ? "Benutzer aktivieren" : "Benutzer deaktivieren"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Diese Änderung wirkt global auf das Benutzerkonto. Mitgliedschaften bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            {userStatusError ? (
              <Alert variant="destructive">
                <AlertTitle>Statusänderung fehlgeschlagen</AlertTitle>
                <AlertDescription>{userStatusError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="grid grid-cols-1 gap-2 rounded-lg border border-border bg-muted/20 p-4 text-sm">
              <InfoRow label="Name" value={userStatusTargetMember?.user?.full_name || "-"} />
              <InfoRow label="E-Mail" value={userStatusTargetMember?.user?.email || "-"} mono />
              <InfoRow label="Mandantenrolle" value={ROLE_LABELS[userStatusTargetMember?.role] || userStatusTargetMember?.role || "-"} />
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Status</span>
                <div className="flex items-center gap-2">
                  {userStatusTargetMember?.user?.is_super_admin ? <Badge variant="outline">Super-Admin</Badge> : null}
                  {userStatusTargetMember?.user?.is_active === false ? (
                    <Badge variant="destructive">Inaktiv</Badge>
                  ) : (
                    <Badge variant="secondary">Aktiv</Badge>
                  )}
                </div>
              </div>
            </div>

            {currentUser?.id && userStatusTargetMember?.user?.id && currentUser.id === userStatusTargetMember.user.id ? (
              <Alert>
                <AlertTitle>Hinweis</AlertTitle>
                <AlertDescription>Der eigene Super-Admin-Zugang kann nicht deaktiviert werden.</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={userStatusSaving}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              variant={userStatusTargetMember?.user?.is_active === false ? "default" : "destructive"}
              disabled={
                userStatusSaving ||
                (currentUser?.id && userStatusTargetMember?.user?.id && currentUser.id === userStatusTargetMember.user.id)
              }
              onClick={(event) => {
                event.preventDefault();
                handleToggleUserStatus();
              }}
            >
              {userStatusSaving
                ? userStatusTargetMember?.user?.is_active === false
                  ? "Aktivieren …"
                  : "Deaktivieren …"
                : userStatusTargetMember?.user?.is_active === false
                  ? "Aktivieren"
                  : "Deaktivieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)] gap-4">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tenant-Liste</CardTitle>
              <CardDescription>Nur für globale Super-Admins sichtbar.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-10 text-center text-sm text-muted-foreground">Wird geladen …</div>
              ) : error ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              ) : rows.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">Keine Mandanten gefunden.</div>
              ) : (
                <div data-testid="admin-tenants-table" className="obsidian overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-max text-sm">
                      <thead className="border-b border-border bg-muted/40">
                        <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                          <th className="px-4 py-3 font-medium">Name</th>
                          <th className="px-4 py-3 font-medium">Slug</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                          <th className="px-4 py-3 font-medium">Projekt</th>
                          <th className="px-4 py-3 font-medium">Farbe</th>
                          <th className="px-4 py-3 font-medium">Mitglieder</th>
                          <th className="px-4 py-3 font-medium">Leads</th>
                          <th className="px-4 py-3 font-medium">Aktion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {rows.map((row) => {
                          const selected = row.id === selectedTenantId;
                          const isActiveTenant =
                            Boolean(tenant) &&
                            (
                              (tenant?.id && row?.id && String(row.id) === String(tenant.id)) ||
                              (tenant?.slug && row?.slug && row.slug === tenant.slug)
                            );
                          return (
                            <tr
                              key={row.id}
                              className={selected ? "bg-muted/40" : "hover:bg-muted/20"}
                            >
                              <td className="px-4 py-3 font-medium">{row.name}</td>
                              <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{row.slug}</td>
                              <td className="px-4 py-3">
                                {row.is_active === false ? (
                                  <Badge variant="destructive">Inaktiv</Badge>
                                ) : (
                                  <Badge variant="secondary">Aktiv</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">{row.default_project || "-"}</td>
                              <td className="px-4 py-3">
                                {row.branding_color ? (
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="h-4 w-4 rounded-full border border-border"
                                      style={{ background: row.branding_color }}
                                    />
                                    <span className="font-mono text-xs">{row.branding_color}</span>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">{row.member_count ?? "-"}</td>
                              <td className="px-4 py-3 text-muted-foreground">{row.lead_count ?? "-"}</td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2 whitespace-nowrap">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    data-testid={`tenant-switch-${row.slug}`}
                                    disabled={isActiveTenant}
                                    onClick={() => {
                                      if (isActiveTenant) return;
                                      handleSwitchToTenant(row);
                                    }}
                                  >
                                    {isActiveTenant ? "Aktiver Mandant" : "In Mandant wechseln"}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant={selected ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setSelectedTenantId(row.id)}
                                  >
                                    Bearbeiten
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="tenant-members-section">
            <CardHeader className="flex flex-col items-start justify-between gap-4 space-y-0 sm:flex-row sm:items-center">
              <div>
                <CardTitle>Mitglieder</CardTitle>
                <CardDescription>
                  {selectedTenant ? `Mitglieder von ${selectedTenant.name}` : "Mandanten in der Liste wählen."}
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setInviteOpen(true)} disabled={!selectedTenantId}>
                  Benutzer einladen
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setAddMemberOpen(true)} disabled={!selectedTenantId}>
                  Bestehenden Benutzer hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!selectedTenantId ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  Wähle einen Mandanten aus, um Mitglieder zu verwalten.
                </div>
              ) : membersLoading ? (
                <div className="py-8 text-center text-sm text-muted-foreground">Mitglieder werden geladen …</div>
              ) : membersError ? (
                <Alert variant="destructive">
                  <AlertTitle>Fehler</AlertTitle>
                  <AlertDescription>{membersError}</AlertDescription>
                </Alert>
              ) : members.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">Keine Mitglieder vorhanden.</div>
              ) : (
                <div className="obsidian overflow-hidden rounded-lg border border-border">
                  <div className="overflow-x-auto">
                    <table className="min-w-max text-sm">
                      <thead className="border-b border-border bg-muted/40">
                        <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                          <th className="px-4 py-3 font-medium">Name</th>
                          <th className="px-4 py-3 font-medium">E-Mail</th>
                          <th className="px-4 py-3 font-medium">Rolle</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                          <th className="px-4 py-3 font-medium">Aktion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {members.map((member) => {
                          const user = member.user || {};
                          const roleLabel = ROLE_LABELS[member.role] || member.role;
                          return (
                            <tr key={member.membership_id}>
                              <td className="px-4 py-3 font-medium">
                                <div className="flex items-center gap-2">
                                  <span>{user.full_name || "-"}</span>
                                  {user.is_super_admin ? <Badge variant="outline">Super-Admin</Badge> : null}
                                </div>
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{user.email || "-"}</td>
                              <td className="px-4 py-3">
                                <Badge variant="secondary">{roleLabel}</Badge>
                              </td>
                              <td className="px-4 py-3">
                                {user.is_active === false ? (
                                  <Badge variant="destructive">Inaktiv</Badge>
                                ) : (
                                  <Badge variant="secondary">Aktiv</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2 whitespace-nowrap">
                                  <Button
                                    type="button"
                                    variant={user.is_active === false ? "secondary" : "outline"}
                                    size="sm"
                                    onClick={() => handleOpenUserStatusDialog(member)}
                                  >
                                    {user.is_active === false ? "Nutzer aktivieren" : "Nutzer deaktivieren"}
                                  </Button>
                                  <Button type="button" variant="outline" size="sm" onClick={() => handleOpenRoleDialog(member)}>
                                    Rolle ändern
                                  </Button>
                                  <Button type="button" variant="destructive" size="sm" onClick={() => handleOpenRemoveDialog(member)}>
                                    Entfernen
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Tenant bearbeiten</CardTitle>
            <CardDescription>
              {selectedTenant ? `Aktuell: ${selectedTenant.name}` : "Mandanten in der Liste wählen."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedTenantId ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Keine Auswahl vorhanden.
              </div>
            ) : detailLoading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Details werden geladen …</div>
            ) : detailError ? (
              <Alert variant="destructive">
                <AlertTitle>Fehler</AlertTitle>
                <AlertDescription>{detailError}</AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSave} className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">Editierbare Felder</div>
                  <Badge variant="secondary">Editierbar</Badge>
                </div>

                {saveError && (
                  <Alert variant="destructive">
                    <AlertTitle>Speichern fehlgeschlagen</AlertTitle>
                    <AlertDescription>{saveError}</AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-1 gap-4">
                  <Field label="Name">
                    <Input
                      value={form.name}
                      onChange={(event) => {
                        const v = event.target.value;
                        setForm((current) => ({ ...current, name: v }));
                        if (nameError && v.trim()) setNameError("");
                      }}
                      placeholder="Tenant-Name"
                    />
                    {nameError && <div className="text-xs text-destructive">{nameError}</div>}
                  </Field>

                  <div className="grid grid-cols-1 md:grid-cols-[120px_minmax(0,1fr)] gap-4">
                    <Field label="Branding-Farbe">
                      <input
                        type="color"
                        value={form.branding_color || "#0F172A"}
                        onChange={(event) => setForm((current) => ({ ...current, branding_color: event.target.value }))}
                        className="h-11 w-full cursor-pointer rounded-lg border border-border bg-card"
                      />
                    </Field>
                    <Field label="HEX-Wert">
                      <Input
                        value={form.branding_color}
                        onChange={(event) => setForm((current) => ({ ...current, branding_color: event.target.value }))}
                        className="font-mono"
                        placeholder="#0F172A"
                      />
                    </Field>
                  </div>

                  <Field label="Default-Projekt">
                    <Input
                      value={form.default_project}
                      onChange={(event) => setForm((current) => ({ ...current, default_project: event.target.value }))}
                      placeholder="z.B. OptiMES Prime"
                    />
                  </Field>

                  <Field label="Sender-Name">
                    <Input
                      value={form.sender_name}
                      onChange={(event) => setForm((current) => ({ ...current, sender_name: event.target.value }))}
                      placeholder="Absender für E-Mails"
                    />
                  </Field>

                  <Field label="Reply-To E-Mail">
                    <Input
                      value={form.reply_to_email}
                      onChange={(event) => setForm((current) => ({ ...current, reply_to_email: event.target.value }))}
                      placeholder="vertrieb@tenant.de"
                    />
                  </Field>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={saving}>
                    <Save className="w-4 h-4" /> {saving ? "Speichern …" : "Speichern"}
                  </Button>
                </div>

                <Separator />

                <div data-testid="tenant-capabilities-section" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">Module & Limits</div>
                    <Badge variant="outline">Super-Admin only</Badge>
                  </div>

                  {capabilitiesSaveError && (
                    <Alert variant="destructive">
                      <AlertTitle>Module & Limits konnten nicht gespeichert werden</AlertTitle>
                      <AlertDescription>{capabilitiesSaveError}</AlertDescription>
                    </Alert>
                  )}

                  <Alert>
                    <AlertTitle>Abhängigkeiten</AlertTitle>
                    <AlertDescription>
                      Public Forms und Widget Embed setzen FyreFunnel voraus. Tenant-Admins dürfen diese Werte nur lesen.
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/20 p-4">
                    <CapabilityToggle
                      dataTestId="tenant-capability-crm-core-enabled"
                      label="CRM Core"
                      description="Grundlegende CRM-Nutzung für diesen Mandanten."
                      checked={capabilitiesForm.crm_core_enabled}
                      onChange={(checked) => updateCapabilityToggle("crm_core_enabled", checked)}
                    />
                    <CapabilityToggle
                      dataTestId="tenant-capability-fyrefunnel-enabled"
                      label="FyreFunnel"
                      description="Master-Switch für Public Forms und spätere Funnel-Erweiterungen."
                      checked={capabilitiesForm.fyrefunnel_enabled}
                      onChange={(checked) => updateCapabilityToggle("fyrefunnel_enabled", checked)}
                    />
                    <CapabilityToggle
                      dataTestId="tenant-capability-public-forms-enabled"
                      label="Public Forms"
                      description="Öffentliche Formulare für diesen Mandanten."
                      checked={capabilitiesForm.public_forms_enabled}
                      disabled={!capabilitiesForm.fyrefunnel_enabled}
                      onChange={(checked) => updateCapabilityToggle("public_forms_enabled", checked)}
                    />
                    <Field label="Max Public Forms">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        data-testid="tenant-capability-max-public-forms"
                        value={capabilitiesForm.max_public_forms}
                        onChange={(event) => updateCapabilityValue("max_public_forms", event.target.value)}
                      />
                    </Field>
                    <CapabilityToggle
                      dataTestId="tenant-capability-widget-embed-enabled"
                      label="Widget Embed"
                      description="Vorbereitung für spätere Embed-Widgets."
                      checked={capabilitiesForm.widget_embed_enabled}
                      disabled={!capabilitiesForm.fyrefunnel_enabled}
                      onChange={(checked) => updateCapabilityToggle("widget_embed_enabled", checked)}
                    />
                    <CapabilityToggle
                      dataTestId="tenant-capability-mail-audit-enabled"
                      label="Mail Audit"
                      description="Schaltet das E-Mail-Protokoll tenantweit frei."
                      checked={capabilitiesForm.mail_audit_enabled}
                      onChange={(checked) => updateCapabilityToggle("mail_audit_enabled", checked)}
                    />
                    <CapabilityToggle
                      dataTestId="tenant-capability-email-tracking-allowed"
                      label="E-Mail Tracking erlaubt"
                      description="Tracking-Einwilligungen dürfen verwendet werden."
                      checked={capabilitiesForm.email_tracking_allowed}
                      onChange={(checked) => updateCapabilityToggle("email_tracking_allowed", checked)}
                    />
                    <CapabilityToggle
                      dataTestId="tenant-capability-marketing-consent-allowed"
                      label="Marketing Consent erlaubt"
                      description="Marketing-Einwilligungen dürfen gespeichert werden."
                      checked={capabilitiesForm.marketing_consent_allowed}
                      onChange={(checked) => updateCapabilityToggle("marketing_consent_allowed", checked)}
                    />
                    <Field label="E-Mail-Kontingent monatlich">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        data-testid="tenant-capability-email-quota-monthly"
                        value={capabilitiesForm.email_quota_monthly}
                        onChange={(event) => updateCapabilityValue("email_quota_monthly", event.target.value)}
                        placeholder="Leer = unbegrenzt"
                      />
                    </Field>
                    <CapabilityToggle
                      dataTestId="tenant-capability-automation-enabled"
                      label="Automation"
                      description="Vorbereitung für spätere Automationen."
                      checked={capabilitiesForm.automation_enabled}
                      onChange={(checked) => updateCapabilityToggle("automation_enabled", checked)}
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      data-testid="tenant-capabilities-save"
                      onClick={handleCapabilitiesSave}
                      disabled={capabilitiesSaving}
                    >
                      <Save className="w-4 h-4" /> {capabilitiesSaving ? "Speichern …" : "Module & Limits speichern"}
                    </Button>
                  </div>
                </div>

                <Separator />

                <div data-testid="tenant-systeminfos-section" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">Systeminfos</div>
                  <div className="flex items-center gap-2">
                    {selectedTenant?.is_active === false ? (
                      <Badge variant="destructive">Inaktiv</Badge>
                    ) : (
                      <Badge variant="secondary">Aktiv</Badge>
                    )}
                    <Badge variant="outline">Read-only</Badge>
                  </div>
                </div>

                {statusError && (
                  <Alert variant="destructive">
                    <AlertTitle>Status ändern fehlgeschlagen</AlertTitle>
                    <AlertDescription>{statusError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-end">
                  {selectedTenant?.is_active === false ? (
                    <Button type="button" onClick={handleToggleTenantStatus} disabled={statusSaving}>
                      {statusSaving ? "Aktivieren …" : "Mandant aktivieren"}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleToggleTenantStatus}
                      disabled={statusSaving}
                    >
                      {statusSaving ? "Deaktivieren …" : "Mandant deaktivieren"}
                    </Button>
                  )}
                </div>

                <Alert>
                  <AlertTitle>Hinweis</AlertTitle>
                  <AlertDescription>
                    Technische Felder sind bewusst geschützt und nicht editierbar: Tenant-ID, Slug, Public-Form-Key und Memberships.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 gap-2 rounded-lg border border-border bg-muted/20 p-4 text-sm">
                  <InfoRow label="Tenant-ID" value={selectedTenant?.id || "-"} mono />
                  <InfoRow label="Name (aktuell)" value={selectedTenant?.name || "-"} />
                  <InfoRow label="Slug" value={selectedTenant?.slug || "-"} mono />
                  <InfoRow label="Public-Form-Key (maskiert)" value={maskPublicFormKey(selectedTenant?.public_form_key)} mono />
                  <InfoRow label="Branding-Farbe" value={selectedTenant?.branding_color || "-"} mono />
                  <InfoRow label="Default-Projekt" value={selectedTenant?.default_project || "-"} />
                  <InfoRow label="Sender-Name" value={selectedTenant?.sender_name || "-"} />
                  <InfoRow label="Reply-To E-Mail" value={selectedTenant?.reply_to_email || "-"} mono />
                  <InfoRow label="Mitglieder" value={String(selectedTenant?.member_count ?? "-")} />
                  <InfoRow label="Leads" value={String(selectedTenant?.lead_count ?? "-")} />
                  <InfoRow label="Erstellt" value={formatDateTime(selectedTenant?.created_at)} />
                  <InfoRow label="Aktualisiert" value={formatDateTime(selectedTenant?.updated_at)} />
                </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SystemBrandingSlot({ title, description, logoUrl, disabled, onUpload, onRemove }) {
  return (
    <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
      <div className="space-y-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <div className="flex min-h-28 items-center justify-center rounded-lg border border-dashed border-border bg-background/70 p-4">
        <AppBrandMark
          logoUrl={logoUrl}
          containerClassName="flex items-center justify-center gap-3 min-w-0 max-w-full"
          badgeClassName="h-12 w-12"
          iconClassName="h-6 w-6"
          textClassName="font-display text-sm font-semibold tracking-tight"
          imageClassName="block h-auto max-h-24 w-auto max-w-[260px] object-contain"
          glowClassName=""
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onUpload(file);
            event.target.value = "";
          }}
        />
        <Button type="button" variant="outline" onClick={onRemove} disabled={disabled || !logoUrl}>
          Entfernen
        </Button>
      </div>
    </div>
  );
}

function CapabilityToggle({
  dataTestId,
  label,
  description,
  checked,
  disabled = false,
  onChange,
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-background/70 p-4">
      <div className="space-y-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-[var(--brand)]"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={dataTestId}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono text-xs text-right" : "text-right"}>{value}</span>
    </div>
  );
}
